import { Warning as WarningIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import DynamicHeightComponent from 'src/utils/dynamicSize.component';
import Spinner from 'src/utils/Loader/spinner';
import { PostDetailsHelper } from 'src/utils/postDetailsHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import BlueskyMobileViewPosts from '../mobile-publishedPosts/blueskyMobileViewPosts';
import NoPostFound from '../noPost.component';
import BlueskyPostDetailView from './BlueskyPostDetailView';

const BlueskyPostGrid = ({ connectedNetworks, handleProfileClick, isShowOfferBanner = false }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToaster();
  const { showRightSidebar } = useRightSidebar();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(true);
  const [mobileLoader, setMobileLoader] = useState(false);
  const [blueskyProfileId, setBlueskyProfileId] = useState('');
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [connectedMedias, setConnectedMedias] = useState([]);
  const [pagination, setPagination] = useState('');
  const [isAccountNeedsReauth, setAccountNeedsReauth] = useState(false);
  const [height, setHeight] = useState(window.innerHeight - 250);

  const hasIntegratationPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.INTEGRATE_PERMISSION
  );

  // Table Variables
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getTableDesk = [
    { id: 'created_time', width: '15%', label: translate('published_post.date_time'), alignRight: false },
    { id: 'image_url', width: '15%', label: translate('published_post.image'), alignRight: false },
    { id: 'message', width: '40%', label: translate('published_post.content'), alignRight: false },
    { id: 'like_count', width: '10%', label: translate('preview.likes'), alignCenter: true },
    { id: 'reply_count', width: '10%', label: translate('bluesky_published_post.replies'), alignCenter: true },
    { id: 'repost_count', width: '10%', label: translate('bluesky_published_post.reposts'), alignCenter: true },
  ];

  const getTableMobile = [
    { id: 'created_time', label: translate('published_post.date_time'), alignRight: false },
    { id: 'message', label: translate('published_post.content'), alignRight: false },
    { id: 'like_count', label: translate('preview.likes'), alignRight: false },
    { id: 'reply_count', label: translate('bluesky_published_post.replies'), alignRight: false },
    { id: 'repost_count', label: translate('bluesky_published_post.reposts'), alignRight: false },
    { id: 'image_url', label: translate('published_post.image'), alignRight: false },
  ];

  const TABLE_HEAD = isMobile ? getTableMobile : getTableDesk;

  useEffect(() => {
    const activeNetworks = connectedNetworks.filter((c) => c.is_active);
    let blueskyId = activeNetworks?.[0]?.id || '';
    setBlueskyProfileId(blueskyId);
    setConnectedMedias(connectedNetworks);

    if (activeNetworks.length === 0) {
      setLoading(false);
      setAccountNeedsReauth(true);
    }

    const handleResize = () => setHeight(window.innerHeight - 300);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [connectedNetworks]);

  useEffect(() => {
    if (blueskyProfileId) getBlueskyPublishedPosts();
  }, [blueskyProfileId]);

  const blueskyPublishedPostSuccessCallback = (data) => {
    setPublishedPosts(data.posts || []);
    setPagination(data.pagination || '');
    setLoading(false);
  };

  const blueskyNextPublishedPostSuccessCallback = (data) => {
    setPublishedPosts((prev) => [...prev, ...(data.posts || [])]);
    setPagination(data.pagination || '');
    setLoading(false);
    setMobileLoader(false);
  };

  const blueskyPublishedPostFailureCallback = (message) => {
    setPublishedPosts([]);
    message && showToast(message, 'e');
    setLoading(false);
  };

  const getBlueskyPublishedPosts = (newRowsPerPage = '10') => {
    setLoading(true);
    const getProps = {
      url: `${postsModuleURL.blueskyPosts}${blueskyProfileId}&post_limit=${newRowsPerPage}`,
      successCallback: blueskyPublishedPostSuccessCallback,
      failureCallback: blueskyPublishedPostFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getNextBlueskyPublishedPosts = () => {
    setLoading(true);
    const getProps = {
      url: `${postsModuleURL.blueskyPosts}${blueskyProfileId}&pagination=${pagination}&post_limit=${rowsPerPage}`,
      successCallback: blueskyNextPublishedPostSuccessCallback,
      failureCallback: blueskyPublishedPostFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getMobileViewNextPublishedPosts = () => {
    setMobileLoader(true);
    const getProps = {
      url: `${postsModuleURL.blueskyPosts}${blueskyProfileId}&pagination=${pagination}&post_limit=10`,
      successCallback: blueskyNextPublishedPostSuccessCallback,
      failureCallback: blueskyPublishedPostFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if ((newPage + 1) * rowsPerPage > publishedPosts.length && pagination) {
      getNextBlueskyPublishedPosts();
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (newRowsPerPage > publishedPosts.length) {
      getBlueskyPublishedPosts(newRowsPerPage);
    }
  };

  const handleOpenPost = (post) => {
    const data = { content: blueskyPostView, data: post };
    showRightSidebar(data);
  };

  const blueskyPostView = (post) => {
    const profile = connectedMedias.find((a) => a.id === blueskyProfileId);
    profile['date'] = CommonHelper.GetDateFormat(post.created_time);

    const postDetails = {
      postId: post.id,
      pageId: blueskyProfileId,
      profile,
      ...post,
      height,
    };
    return <BlueskyPostDetailView {...postDetails} />;
  };

  const handleBlueskyPage = (id) => {
    setBlueskyProfileId(id);
    setRowsPerPage(10);
    setPage(0);
  };

  const navigateToIntegration = () => navigate('/integration');

  const getNetworkChip = () => {
    return connectedMedias.map((c) => {
      const isActive = c.id === blueskyProfileId;
      const isProfileActive = c.is_active;
      const showReauth = !isProfileActive && hasIntegratationPermission;

      return (
        <Chip
          key={c.id}
          style={{
            borderRadius: 8,
            opacity: isActive ? 1 : 0.9,
            ...(!isProfileActive && {
              backgroundColor: theme.palette.error.lighter,
              border: `1px solid ${theme.palette.error.main}`,
            }),
            ...(isActive &&
              isProfileActive && {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                fontWeight: 800,
              }),
          }}
          sx={{ m: 0.4 }}
          onClick={isProfileActive ? () => handleBlueskyPage(c.id) : showReauth ? () => handleProfileClick(c) : undefined}
          deleteIcon={!isProfileActive ? <WarningIcon /> : undefined}
          onDelete={showReauth ? () => handleProfileClick(c) : undefined}
          avatar={
            <Avatar
              src={c.profile_image || c.profile_name}
              alt={c.profile_name || c.id}
              sx={{
                height: 40,
                width: 40,
                bgcolor: isActive ? 'white' : theme.palette.primary.main,
                ...(!isProfileActive && { bgcolor: theme.palette.error.main, color: 'white' }),
              }}
              style={{ color: isActive ? theme.palette.primary.main : 'white' }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {c.profile_name || c.id}
              {!isProfileActive && (
                <Typography variant="caption" sx={{ ml: 1, color: theme.palette.error.dark }}>
                  ({translate('report.needs_auth')})
                </Typography>
              )}
            </Box>
          }
        />
      );
    });
  };

  const getTableBodyData = () => {
    return publishedPosts.length > 0 ? (
      publishedPosts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
        <StyledComponent.StyledTableRow key={row.id} onClick={() => handleOpenPost(row)}>
          <StyledComponent.StyledTableCell align="left">
            {CommonHelper.getTableDateAndTime(row.created_time)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="left">
            {PostDetailsHelper.getPostMedias(row.media_url)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="left">
            {PostDetailsHelper.GetPostContent(row.message, 400)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="center">
            {CommonHelper.formatNumber(row.like_count)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="center">
            {CommonHelper.formatNumber(row.reply_count)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="center">
            {CommonHelper.formatNumber(row.repost_count)}
          </StyledComponent.StyledTableCell>
        </StyledComponent.StyledTableRow>
      ))
    ) : (
      <NoPostFound />
    );
  };

  const getGrid = () => (
    <DynamicHeightComponent>
      <Scrollbar>
        {isMobile ? (
          <BlueskyMobileViewPosts
            publishedPosts={publishedPosts}
            getMobileViewNextPublishedPosts={getMobileViewNextPublishedPosts}
            mobileLoader={mobileLoader}
            pagination={pagination}
          />
        ) : (
          <TableContainer sx={{ height: isShowOfferBanner ? 'calc(100vh - 340px)' : 'calc(100vh - 290px)' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order="asc"
                orderBy="created_time"
                headLabel={TABLE_HEAD}
                rowCount={publishedPosts.length}
                isShowCheckbox={false}
              />
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Spinner height={height} />
                    </TableCell>
                  </TableRow>
                ) : (
                  getTableBodyData()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!isMobile && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20, 30, 50]}
            count={publishedPosts.length + 1}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={() => ''}
          />
        )}
      </Scrollbar>
    </DynamicHeightComponent>
  );

  return (
    <Box>
      <Card sx={{ minHeight: 'calc(100vh - 150px)', maxHeight: 'calc(100vh - 150px)' }}>
        {connectedMedias.length > 0 ? (
          <>
            <Card sx={{ mb: 2, p: 2, maxHeight: 100, overflowY: 'auto' }}>
              {getNetworkChip()}
            </Card>
            <Card sx={{ boxShadow: isMobile && publishedPosts?.length > 0 && 'none', backgroundColor: isMobile && publishedPosts?.length > 0 && 'transparent' }}>
              {isLoading ? (
                <Spinner height={height} />
              ) : publishedPosts?.length === 0 ? (
                <NoPostFound
                  height={isMobile ? 'calc(100vh - 330px)' : isShowOfferBanner ? 'calc(100vh - 380px)' : 'calc(100vh - 330px)'}
                  contentText={
                    isAccountNeedsReauth
                      ? translate('published_post.need_re_authentication')
                      : translate('published_post.no_post_available')
                  }
                />
              ) : (
                getGrid()
              )}
            </Card>
          </>
        ) : isLoading ? (
          <Spinner height={height} />
        ) : (
          <Card sx={{ height: height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LoadingButton variant="contained" onClick={navigateToIntegration}>
              {translate('report.connect_social_accounts')}
            </LoadingButton>
          </Card>
        )}
      </Card>
    </Box>
  );
};

export default BlueskyPostGrid;